import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Lock } from "lucide-react";

const BG = process.env.REACT_APP_LOGIN_BG_URL || "/login-bg.jpg";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.name, form.password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left / branding */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-zinc-800 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BG})` }}
        />
        <div className="absolute inset-0 bg-black/75" />
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 grid place-items-center bg-primary/20 border border-primary/40 rounded-sm">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <div>
            <div className="font-heading font-black text-xl tracking-tight text-white">SADCR</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              Dept. of Corrections & Rehabilitation
            </div>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-heading text-4xl font-black uppercase tracking-tight text-white leading-tight">
            San Andreas
            <br />
            Corrections System
          </h2>
          <p className="text-zinc-400 text-sm mt-4 max-w-sm">
            Zentrales Verwaltungssystem für Informationen, Inhaftierungen und Einsätze der
            Staatsfraktion SADCR.
          </p>
        </div>
        <div className="relative font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
          Restricted Access · Authorized Personnel Only
        </div>
      </div>

      {/* Right / form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm sadcr-fade">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 grid place-items-center bg-primary/15 border border-primary/40 rounded-sm">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div className="font-heading font-black text-lg tracking-tight text-zinc-100">SADCR</div>
          </div>

          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
            Anmeldung
          </div>
          <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-zinc-100 mb-8">
            Officer Login
          </h1>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Name</Label>
              <Input
                data-testid="login-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                required
                autoComplete="username"
                className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Passwort</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                required
                autoComplete="current-password"
                className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2">
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-sm bg-primary hover:bg-blue-600 text-white gap-2 h-11"
            >
              <Lock className="h-4 w-4" />
              {loading ? "Bitte warten…" : "Anmelden"}
            </Button>
          </form>

          <p className="mt-6 text-xs text-zinc-600">
            Zugänge werden ausschließlich durch Leitung/Aufsicht vergeben.
          </p>
        </div>
      </div>
    </div>
  );
}
